import {Box, Chip, Stack, Typography} from "@mui/material"
import {Trip} from "./Trip";
import {useNavigate} from "react-router";
import {PATH} from "../../routing/routes";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import {displayDate, displayDuration} from "../../utils";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

interface TripWithPhotosComponentProps {
    trip: Trip;
}

const TripWithPhotosComponent = ({trip}: TripWithPhotosComponentProps) => {
    const navigate = useNavigate();

    const handleNavigate = () => {
        window.scrollTo({top: 0, behavior: 'smooth'});
        navigate(PATH.SENIOR_TRIP, {state: {trip}})
    }

    if (!trip.photos) {
        return null
    }

    return (
        <Stack
            direction="column"
            onClick={handleNavigate}
            sx={{
                width: "100%",
                overflow: "hidden",
                borderRadius: 2,
                cursor: "pointer",
                "&:hover img": {
                    transform: "scale(1.08)",
                },
            }}
        >
            {/* Photos + Badges wrapper */}
            <Box sx={{position: "relative", width: "100%"}}>

                {/* Photos displayed horizontally */}
                <Stack direction="row" sx={{width: "100%"}}>
                    {/*takes first 4 photos attached to the trip*/}
                    {/*be sure to have at least 4 photos!*/}
                    {(trip.photos ?? []).slice(0, 4).map((photo, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: {
                                    xs: index >= 2 ? "none" : "block",
                                    sm: index >= 3 ? "none" : "block",
                                    md: "block",
                                },
                                width: {
                                    xs: "50%",
                                    sm: "33.33%",
                                    md: "25%",
                                },
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                component="img"
                                src={photo as string}
                                alt={`${trip.name} photo ${index + 1}`}
                                sx={{
                                    width: "100%",
                                    aspectRatio: "5 / 4",
                                    objectFit: "cover",
                                    transition: "transform 0.5s ease",
                                    display: "block",
                                }}
                            />
                        </Box>
                    ))}
                </Stack>

                {/* Floating badges — bottom-right corner over the photos */}
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 12,
                    }}
                >
                    {/* Info badges */}
                    <Stack
                        direction="column"
                        spacing={0.8}
                        sx={{
                            position: "absolute",
                            bottom: 10,
                            right: 12,
                        }}
                    >
                        {trip.days.length && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.8,
                                    px: 3.4,
                                    py: 0.7,
                                    borderRadius: "999px",
                                    backgroundColor: "rgba(0,0,0,0.45)",
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.2)",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                }}
                            >
                                <WbSunnyOutlinedIcon sx={{fontSize: "2rem", color: "#fff"}}/>
                                <Typography sx={{
                                    color: "#fff",
                                    fontWeight: 600,
                                    fontSize: "2rem",
                                    lineHeight: 1,
                                    whiteSpace: "nowrap"
                                }}>
                                    {displayDuration(trip.days.length)}
                                </Typography>
                            </Box>
                        )}

                        {trip.price && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.8,
                                    px: 1.4,
                                    py: 0.7,
                                    borderRadius: "999px",
                                    backgroundColor: (theme) => theme.palette.primary.main,
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.2)",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                }}
                            >
                                <CreditCardIcon sx={{color: "#fff"}}/>
                                <Typography sx={{
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: "2rem",
                                    lineHeight: 1,
                                    whiteSpace: "nowrap"
                                }}>
                                    {trip.price}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Box>

            {/* Trip Name Box */}
            <Box sx={{
                px: 2,
                py: 1.5,
                backgroundColor: (theme) => theme.palette.background.paper,
                textTransform: "uppercase"
            }}>
                <Stack direction="column">
                    <Typography variant="h6" sx={{fontWeight: 800}}>
                        {trip.name}
                    </Typography>

                    {/* Trip dates */}
                    <Stack direction="row" sx={{flexWrap: "wrap", gap: 0.8}}>
                        {trip.dates &&
                            trip.dates.map((date) => (
                                <Chip
                                    icon={<CalendarMonthIcon/>}
                                    label={displayDate(date, trip.days.length)}
                                />
                            ))}
                    </Stack>

                </Stack>

            </Box>
        </Stack>
    );
};

export default TripWithPhotosComponent;